import { DateTime } from 'luxon';
import { type BandsintownEvent } from '../utils/bandsintown.server.ts'

interface Params {
  events: BandsintownEvent[],
  color: String
}

export default function TourDateTable({ events = [], color = "white" }: Params) {
  const colorClasses = color === 'black'
    ? { text: 'text-black', textMuted: 'text-black/60', border: 'border-black/15', tableBg: 'bg-none' }
    : { text: 'text-white', textMuted: 'text-white/60', border: 'border-white/15', tableBg: 'bg-white/10 backdrop-blur-xs' };

  if (events.length === 0) {
    return (
      <div id="tourDatesTable" className={`w-11/12 lg:w-7/8 2xl:w-[1500px] py-8 px-3 md:p-8 ${colorClasses.text} border-solid border-2 ${colorClasses.border} rounded-xl ${colorClasses.tableBg} flex flex-col items-center gap-2`}>
        <p className="text-lg md:text-xl font-semibold">No upcoming shows</p>
        <p className={`text-sm md:text-base ${colorClasses.textMuted}`}>Check back soon for new tour dates.</p>
      </div>
    )
  }

  return (
    <div role="table" id="tourDatesTable" className={`grid grid-cols-1 md:grid-cols-4 gap-x-3 md:gap-x-4 py-4 px-3 md:px-8 md:py-4 mx-4 mb-4 md:mb-0 text-sm sm:text-base md:text-lg border-solid border-2 border-white/15 ${colorClasses.text} rounded-xl ${colorClasses.tableBg}`}>
      <div role="rowgroup" className="contents">
        <div role="row" className="contents">
          <div role="columnheader" className="sr-only">Date</div>
          <div role="columnheader" className="sr-only">Location</div>
          <div role="columnheader" className="sr-only">Tickets</div>
        </div>
      </div>
      <div role="rowgroup" className="contents">
        {events.map((event: BandsintownEvent, index: number) => {
          const dt = DateTime.fromISO(event.datetime);
          const date = dt.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
          const dayOfWeek = dt.toFormat('EEE')
          const dateString = dt.toFormat('MMM d')
          const year = dt.toFormat('y')
          const venue = event.venue.name ?? event.venue.location ?? 'this show';

          let callToAction;
          if (event.offers.length) {
            callToAction = (
              <a
                className="inline-block w-full md:w-auto text-white font-bold uppercase py-2 md:px-8 mt-4 md:mt-0 md:text-base bg-orange rounded-xl transition-all duration-300 ease-in-out shadow-none hover:shadow-orange/20 hover:shadow-md hover:scale-105"
                href={event.offers[0]?.url || ''}
                aria-label={`Buy tickets for ${venue} on ${date}`}
              >
                Tickets
              </a>
            );
          } else {
            callToAction = (
              <a
                className={`inline-block w-full md:w-auto ${color === 'white' ? 'text-white border-white' : 'text-black border-black'} font-bold uppercase py-2 md:px-8 mt-4 md:mt-0 md:text-base justify-self-end border-2 rounded-xl transition-all duration-300 ease-in-out shadow-none hover:shadow-orange/20 hover:shadow-md hover:scale-105`}
                href={`${event.url}&trigger=notify_me`}
                aria-label={`Get notified for ${venue} on ${date}`}
              >
                Notify
              </a>
            );
          }

          return (
            <div key={event.id ?? index} role="row" className={`md:grid md:grid-cols-subgrid md:col-span-4 md:items-center py-3 ${index > 0 ? `border-t ${colorClasses.border}` : ''}`}>
              <div role="cell" className="justify-self-start text-left font-bold">
                <p className="font-bold text-xl">{dateString}</p>
                <p className="font-light text-xs hidden md:inline">{`${dayOfWeek} - ${year}`}</p>
              </div>
              <div role="cell" className="md:col-span-2 md:justify-self-start text-left font-semibold">
                <p className="font-semibold">{event.venue.location}</p>
                <p className="font-light italic">{event.venue.name}</p>
              </div>
              <div role="cell" className="w-full md:w-1/2 justify-self-center lg:justify-self-end text-center">
                {callToAction}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  )
}
